import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Briefcase, Building, User } from 'lucide-react';
import { useUserStore } from '@/store';
import { useUpdateProfile } from '@/api-client/use-update-profile';
import { useSaveProfileData } from '../hooks/use-onboarding';
import type { StepProfileProps } from '../types/onboarding.types';

export function StepProfile({ data, onChange, onComplete, isLoading }: StepProfileProps) {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);

  const { updateMeAsync } = useUpdateProfile();
  const saveProfileMutation = useSaveProfileData();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.itemId) return;

    setIsSaving(true);

    try {
      await Promise.all([
        updateMeAsync({
          firstName: data.firstName,
          lastName: data.lastName,
        }),
        saveProfileMutation.mutateAsync({
          userId: user.itemId,
          jobTitle: data.jobTitle.trim(),
          department: data.department.trim(),
        }),
      ]);

      onComplete();
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof typeof data, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const isValid = data.firstName.trim() && data.lastName.trim();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <User className="h-8 w-8 text-primary" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {t('ONBOARDING_STEPS_PROFILE_TITLE')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('ONBOARDING_STEPS_PROFILE_DESCRIPTION')}
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">{t('ONBOARDING_FORM_FIRSTNAME')} *</Label>
            <Input
              id="firstName"
              type="text"
              value={data.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder={t('ONBOARDING_FORM_FIRSTNAMEPLACEHOLDER')}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">{t('ONBOARDING_FORM_LASTNAME')} *</Label>
            <Input
              id="lastName"
              type="text"
              value={data.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder={t('ONBOARDING_FORM_LASTNAMEPLACEHOLDER')}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="jobTitle" className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-muted-foreground" />
            {t('ONBOARDING_FORM_JOBTITLE')}
          </Label>
          <Input
            id="jobTitle"
            type="text"
            value={data.jobTitle}
            onChange={(e) => handleChange('jobTitle', e.target.value)}
            placeholder={t('ONBOARDING_FORM_JOBTITLEPLACEHOLDER')}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="department" className="flex items-center gap-2">
            <Building className="h-4 w-4 text-muted-foreground" />
            {t('ONBOARDING_FORM_DEPARTMENT')}
          </Label>
          <Input
            id="department"
            type="text"
            value={data.department}
            onChange={(e) => handleChange('department', e.target.value)}
            placeholder={t('ONBOARDING_FORM_DEPARTMENTPLACEHOLDER')}
          />
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          disabled={!isValid || isSaving || isLoading || !user?.itemId}
          className="min-w-30"
        >
          {isSaving ? t('ONBOARDING_BUTTONS_SAVING') : t('ONBOARDING_BUTTONS_CONTINUE')}
        </Button>
      </div>
    </form>
  );
}
